/** @file This file exports a client for the v2 version of our API. */
import { FetchHttpHandler } from '@smithy/fetch-http-handler';
import { Apiv2Client } from '@codegrade/apiv2-client';
import router from './router';
import * as storeMod from './store';
import { signal } from './utils';
/** Redirect the user to the login page (unless already there). */
function redirectToLogin() {
    if (router.currentRoute.name !== 'login') {
        router.logoutAndRedirectAfterLogin();
    }
}
/** A custom implementation of the fetch handler. */
class Handler extends FetchHttpHandler {
    /**
     * Handle a request with fetch.
     *
     * @param request The request to handle.
     * @param opts Optional options on how to handle the request.
     * @returns Whatever `FetchHttpHandle` returns.
     */
    async handle(request, opts) {
        const resp = await super.handle(request, opts);
        if (resp.response.statusCode === 401 && request.method.toUpperCase() === 'GET') {
            const token = storeMod.UserStore.dangerousSession().extract();
            const requestToken = request.headers.Authorization?.replace(/^Bearer\s*/, '');
            // Only redirect when the 401 is for the current session. A mismatch means
            // the request was sent with a stale token (e.g. after logout).
            if (token == null || token.accessToken === requestToken) {
                redirectToLogin();
            }
        }
        return resp;
    }
}
const requestHandler = new Handler({ keepAlive: true });
let innerClient;
// Do not replace this with using the option for `token` to be a method on the `Apiv2Client`. That
// method is cached for an unspecified amount of time, which causes login/logouts or leave/enter
// student view to use the wrong token.
signal.STORE_CREATED.register(async (store) => {
    store.watch(state => state.user.loggedInUser.extract()?.session.token ?? '', token => {
        innerClient = new Apiv2Client({
            region: 'eu-west-1',
            endpoint: `${window.location.origin}/api/v2`,
            token: { token },
            requestHandler,
            cacheMiddleware: true,
        });
    }, { immediate: true });
});
/**
 * A client for the v2 version of the API.
 *
 * It is safe to pass this value as a prop in Vue components.
 */
export const client = Object.freeze({
    // @ts-expect-error As `send` is overloaded Typescript doesn't understand this magic.
    send: (...args) => innerClient.send(...args),
    getRawClient: () => innerClient,
});
